import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { first, takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { UploadHiddenInputComponent } from '../upload-hidden-input/upload-hidden-input.component';
import {
  CUSTOM_OVERLAY_VIEWS,
  CustomOverlayService,
} from '../../../services/custom-overlay.service';
import { TooltipDirective } from '@app/directives/tooltip.directive';

/**
 * Component that opens a file upload window on click
 * Can return selected files as an array of File objects, or as a raw event, or cropped image
 * @deprecated - use InputUploadComponent instead for file uploads. However, this component uses image cropper functionality that InputUploadComponent does not have.
 */
@Component({
  selector: 'app-upload-window',
  templateUrl: './upload-window.component.html',
  styleUrls: ['./upload-window.component.scss'],
  imports: [MatTooltipModule, NgClass, UploadHiddenInputComponent, TooltipDirective],
})
export class UploadWindowComponent implements OnDestroy {
  @ViewChild('hiddenFileInput') hiddenFileInput: UploadHiddenInputComponent;
  @Input() disabled = false;
  @Input() allowMultipleFiles = false;
  @Input() allowSameFileUpload = true;
  @Input() hideUploadIcon = false;
  @Input() iconClass = 'icon-upload';
  @Input() acceptFileTypes: string;
  @Input() tooltipText = 'Upload File';
  @Output() chosenFiles: EventEmitter<File[]> = new EventEmitter<File[]>();
  @Output() chosenFilesRaw: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() croppedImage: EventEmitter<Blob> = new EventEmitter<Blob>();

  private isDestroyed$ = new Subject<void>();
  subscription: Subscription;

  constructor(private overlayService: CustomOverlayService) {}

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  registerFileAdded(event: File[]) {
    this.chosenFiles.emit(event);
  }

  registerFileAddedRaw($event: Event) {
    this.chosenFilesRaw.emit($event);
  }

  openWindow() {
    this.hiddenFileInput.openFileUploadWindow();
  }

  openCropper(files: File[]) {
    if (!files?.length) {
      return;
    }

    const file = files[0];
    this.overlayService.openOverlay('screen-center', file, CUSTOM_OVERLAY_VIEWS.CROPPER, {
      closeOnBackdropClick: false,
      backdropClass: undefined,
    });

    if (this.subscription?.closed === false) {
      this.subscription?.unsubscribe();
    }

    this.subscription = this.overlayService.outputData$
      .pipe(takeUntil(this.isDestroyed$), first())
      .subscribe((blob: Blob) => {
        if (!blob) {
          return;
        }

        this.croppedImage.emit(blob);
        this.overlayService.closeOverlay();
      });
  }
}
